import React, { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { useDispatch, useSelector } from "react-redux"
import { FormattedMessage, useIntl } from "react-intl"
import { Controller, useForm } from "react-hook-form"
import classNames from "classnames"

// Components
import { Select, SignupInput } from "../../../common"
import { CountryFlagSelect } from "../../../common/PhoneNumberInput/components"

// Redux
import { changeTeamDetailsAction } from "../../../../redux/slices/authSlice"

// Services
import { updateDetails } from "../../../../services/team"

// Constants
import { TEAM_TYPES, teamTypeOptions } from "../../../../constants"

const BillingDetailsForm = () => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  const auth = useSelector((state) => state.auth)

  const [loading, setLoading] = useState(false)
  const [isSafeToResetForm, setIsSafeToResetForm] = useState(false);
  const [currentTeamType, setCurrentTeamType] = useState(teamTypeOptions.find(
    (option) => option.value === auth.team.type
  ) || teamTypeOptions[0])
  
  const [editable, setEditable] = useState({
    type: false,
    name: false,
    registrationNumber: false,
    billingEmail: false,
    billingCountryCode: false,
    billingAddress: false,
  })

  const isReadOnly = auth.role.role === "TeamMember"

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    getValues,
    reset,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      type: currentTeamType,
      name: auth.team.name,
      registrationNumber: auth.team.registrationNumber,
      billingEmail: auth.team.billingEmail,
      billingCountryCode: auth.team.billingCountryCode,
      billingAddress: auth.team.billingAddress,
    },
  })

  const [
    type, name, registrationNumber, billingEmail, billingCountryCode, billingAddress,
  ] = watch([
    "type", "name", "registrationNumber", "billingEmail", "billingCountryCode", "billingAddress"
  ]);

  useEffect(() => {
    if (!isSafeToResetForm) return;

    const values = getValues()
    reset(values); // asynchronously reset your form values
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [isSafeToResetForm])

  const onSubmit = async (values) => {
    if (loading) return

    //
    try {
      const toUpdate = {}

      if (editable.type) toUpdate.type = values.type.value;
      if (editable.name) toUpdate.name = values.name;
      if (editable.registrationNumber) toUpdate.registrationNumber = values.registrationNumber;
      if (editable.billingEmail) toUpdate.billingEmail = values.billingEmail;
      if (editable.billingCountryCode) toUpdate.billingCountryCode = values.billingCountryCode.value;
      if (editable.billingAddress) toUpdate.billingAddress = values.billingAddress;

      if (Object.keys(toUpdate).length === 0) return;

      setLoading(true)
      await updateDetails(toUpdate)

      setCurrentTeamType(values.type);
      setIsSafeToResetForm(true);
      setEditable({
        type: false,
        name: false,
        registrationNumber: false,
        billingEmail: false,
        billingCountryCode: false,
        billingAddress: false,
      })

      dispatch(changeTeamDetailsAction(values))
      toast.success(formatMessage({ id: "toast.billingDetailsUpdated" }))
    } catch {
      toast.error(formatMessage({ id: "toast.somethingWentWrongTryAgain" }))
    }
    setLoading(false)
    //
  }

  const handleCancel = () => {
    setIsSafeToResetForm(true);
    setEditable({
      type: false,
      name: false,
      registrationNumber: false,
      billingEmail: false,
      billingCountryCode: false,
      billingAddress: false,
    })
  }

  const isPersonalTeamType = type.value === TEAM_TYPES.Personal;
  const dataHasBeenEdited = Object.values(editable).some((value) => value);
  const disableSubmitButton = loading || !dataHasBeenEdited;

  return (
    <form className="details-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="details-form__inputs">
        <div className="details-form__inputs">
          <Controller
            control={control}
            name="type"
            rules={{
              required: formatMessage({ id: "common.teamTypeRequired" }),
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                value={value}
                onChange={onChange}
                name={"type"}
                placeholder={formatMessage({ id: "common.teamType" })}
                options={teamTypeOptions}
                showEditButton={!isReadOnly}
                editButtonText={formatMessage({ id: "common.changeTeamType" })}
                editable={editable.type}
                setEditable={() => setEditable((prev) => ({ ...prev, type: true }))}
                error={errors.type?.message}
                disabled={isReadOnly || !editable.type}
              />
            )}
          />
        </div>

        <SignupInput
            name="name"
            placeholder={formatMessage({ id: `common.${isPersonalTeamType ? "whatsYourName" : "whatsYourCompanyName"}` })}
            label={formatMessage({ id: `common.${isPersonalTeamType ? "whatsYourName" : "whatsYourCompanyName"}` })}
            register={register("name", {
              required: formatMessage({ id: "common.nameRequired" }),
            })}
            error={errors.name?.message}
            touched={touchedFields.name}
            value={name}
            showEditButton={!isReadOnly}
            editable={editable.name}
            editButtonText={formatMessage({ id: `common.${isPersonalTeamType ? "editYourName" : "editYourCompanyName"}` })}
            disabled={isReadOnly}
            setEditable={() => setEditable((prev) => ({ ...prev, name: true }))}
        />

        <SignupInput
          {...{
            name: "registrationNumber",
            placeholder: formatMessage({ id: "common.taxID" }),
            label: formatMessage({ id: "common.taxID" }),
            register: register("registrationNumber", {
              required: formatMessage({
                id: "common.taxIDRequired",
              }),
            }),
            error: errors.registrationNumber?.message,
            touched: touchedFields.registrationNumber,
            value: registrationNumber,
            showEditButton: !isReadOnly,
            editable: editable.registrationNumber,
            editButtonText: formatMessage({ id: "common.editTaxID" }),
            disabled: isReadOnly,
            setEditable: () =>
              setEditable((prev) => ({ ...prev, registrationNumber: true })),
          }}
        />

        <SignupInput
          {...{
            name: "billingEmail",
            placeholder: formatMessage({ id: "common.billingEmail" }),
            label: formatMessage({ id: "common.email" }),
            register: register("billingEmail", {
              required: false,
              pattern: {
                value: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/i,
                message: formatMessage({ id: "common.invalidEmailAddress" }),
              },
            }),
            error: errors.billingEmail?.message,
            touched: touchedFields.billingEmail,
            value: billingEmail,
            showEditButton: !isReadOnly,
            editable: editable.billingEmail,
            editButtonText: formatMessage({ id: "common.editBillingEmail" }),
            setEditable: () => setEditable((prev) => ({ ...prev, billingEmail: true })),
          }}
        />

        <div className="signup-input">
          <Controller
            control={control}
            name="billingCountryCode"
            render={({ field: { onChange, value } }) => (
              <CountryFlagSelect
                useCountryName
                value={value}
                onChange={onChange}
                name={"billingCountryCode"}
                placeholder={formatMessage({ id: "common.selectACountry" })}
                showEditButton={!isReadOnly}
                editButtonText={formatMessage({ id: "common.changeSelectedCountry" })}
                editable={editable.billingCountryCode}
                setEditable={() => setEditable((prev) => ({ ...prev, billingCountryCode: true }))}
                error={errors.billingCountryCode?.message}
                disabled={isReadOnly || !editable.billingCountryCode}
              />
            )}
          />
        </div>

        <SignupInput
          {...{
            name: "billingAddress",
            placeholder: formatMessage({ id: "common.billingAddress" }),
            label: formatMessage({ id: "common.billingAddress" }),
            register: register("billingAddress", {
              required: false,
            }),
            error: errors.billingAddress?.message,
            value: billingAddress,
            showEditButton: true,
            editable: editable.billingAddress,
            editButtonText: formatMessage({ id: "common.editBillingAddress" }),
            setEditable: () => setEditable((prev) => ({ ...prev, billingAddress: true })),
            touched: touchedFields.billingAddress,
            disabled: isReadOnly || !editable.billingAddress,
          }}
        />
      </div>

      {!isReadOnly && (
        <div className="details-form__buttons">
          <button
            className={classNames("btn btn--small primary", {
              disabled: disableSubmitButton
            })}
          >
            <FormattedMessage id="common.saveChanges" />
          </button>
          <button
            className="btn btn--small tertiary"
            type="button"
            onClick={handleCancel}
          >
            <FormattedMessage id="common.cancel" />
          </button>
        </div>
      )}
    </form>
  )
}

export default BillingDetailsForm
